import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useQuery } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { boxService } from '@/services/boxService';
import { userService } from '@/services/userService';
import { alarmService } from '@/services/alarmService';
import { projectMembershipService } from '@/services/projectMembershipService';
import { formatDate, getProjectLogoUrl } from '@/utils';
import { Project, ProjectMembership, Alarm } from '@/types';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';

interface ProjectWithStats extends Project {
  totalEquipments?: number;
  offlineEquipments?: number;
  manager?: string;
  managerKind?: 'admin' | 'member' | 'none';
}

// Component to display project logo with fallback
function ProjectLogoIcon({ project }: { project: Project }) {
  const [logoError, setLogoError] = useState(false);

  if (project.logo && !logoError) {
    return (
      <div className="size-12 rounded-lg overflow-hidden flex items-center justify-center bg-white dark:bg-slate-800 shadow-lg">
        <img
          src={getProjectLogoUrl(project.logo)}
          alt={project.title}
          className="w-full h-full object-cover"
          onError={() => setLogoError(true)}
        />
      </div>
    );
  }

  return (
    <div className="size-12 rounded-lg bg-gradient-to-tr from-primary to-emerald-400 flex items-center justify-center text-slate-900 shadow-lg">
      <span className="material-icons-outlined">apartment</span>
    </div>
  );
}

export function RootDashboardPage() {
  const navigate = useNavigate();
  const { user, isRoot, isAdmin } = useAuthStore();
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Admin UI: allow ROLE_ADMIN and ROLE_ROOT
  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  // Fetch projects (limit to 5 for the table)
  const {
    data: paginatedResponse,
    isLoading: projectsLoading,
  } = useQuery({
    queryKey: ['projects', 'dashboard'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 5 }),
    retry: 1,
  });

  const projects = paginatedResponse?.data || [];
  const totalProjects = paginatedResponse?.totalItems || 0;

  // Fetch all projects for stats (with ordering by createdAt DESC from API)
  const { data: allProjectsResponse } = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
    retry: 1,
  });

  // Fetch global boxes statistics from dedicated endpoint
  const { data: globalBoxStats = { total: 0, online: 0, offline: 0 } } = useQuery({
    queryKey: ['boxes', 'global-stats'],
    queryFn: () => projectService.getGlobalBoxesStats(),
    retry: 1,
  });

  // Fetch all boxes for project stats (needed for project-by-project stats in table)
  const { data: allBoxes } = useQuery({
    queryKey: ['boxes', 'all'],
    queryFn: async () => {
      // Fetch all boxes with pagination
      const { apiClient } = await import('@/config/api');
      let allBoxes: any[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await apiClient.get('/boxes', {
          params: {
            page,
            itemsPerPage: 100,
            'groups[]': ['box:read'],
          },
        });
        const data = response.data;
        const boxes = data['hydra:member'] || data['member'] || [];
        allBoxes = [...allBoxes, ...boxes];
        
        const view = data['hydra:view'] || data['view'];
        hasMore = !!view?.['hydra:next'];
        page++;
      }

      return allBoxes;
    },
    retry: 1,
  });

  // Fetch all users
  const { data: usersResponse } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => userService.getUsers({ page: 1, itemsPerPage: 1000 }),
    retry: 1,
  });

  // Fetch recent alarms
  const { data: alarmsResponse } = useQuery({
    queryKey: ['alarms', 'recent'],
    queryFn: () => alarmService.getAlarms({ page: 1, itemsPerPage: 5 }),
    retry: 1,
  });

  // Fetch all memberships at once (more efficient)
  const { data: membershipsData } = useQuery({
    queryKey: ['project_memberships', 'all'],
    queryFn: async () => {
      try {
        const { apiClient } = await import('@/config/api');
        const response = await apiClient.get('/project_memberships', {
          params: {
            'groups[]': ['membership:read', 'user:read'],
            itemsPerPage: 1000,
          },
        });
        const data = response.data;
        return data['hydra:member'] || data['member'] || [];
      } catch (error) {
        console.error('Error fetching memberships:', error);
        return [];
      }
    },
    retry: 1,
  });

  // Calculate stats from dedicated endpoint
  const totalEquipments = globalBoxStats.total;
  const onlineEquipments = globalBoxStats.online;
  const onlinePercentage = totalEquipments > 0 ? Math.round((onlineEquipments / totalEquipments) * 100) : 0;
  const totalUsers = usersResponse?.totalItems || 0;

  const usersById = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of usersResponse?.data ?? []) {
      if (typeof u.id === 'number') {
        map.set(u.id, u.fullName || u.email || `Utilisateur #${u.id}`);
      }
    }
    return map;
  }, [usersResponse?.data]);

  const getProjectIdFromMembership = (m: ProjectMembership): number | null => {
    const p: any = (m as any)?.project;
    if (!p) return null;
    if (typeof p === 'object' && typeof p.id === 'number') return p.id;
    if (typeof p === 'string') return parseInt(p.split('/').pop() || '0', 10) || null;
    return null;
  };

  const getMembershipTimestamp = (m: ProjectMembership): number => {
    const anyM: any = m as any;
    const raw = anyM.updatedAt ?? anyM.createdAt ?? null;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const getResponsibleForProject = (projectId: number, project?: Project): { name: string; kind: 'admin' | 'member' | 'none' } => {
    // 1) Prefer explicit memberships on project if present
    const fromProject: ProjectMembership[] =
      project?.memberships && Array.isArray(project.memberships)
        ? (project.memberships as any[]).filter((m) => typeof m === 'object' && m !== null && typeof (m as any).role === 'string')
        : [];

    const fromGlobal: ProjectMembership[] =
      membershipsData && Array.isArray(membershipsData)
        ? (membershipsData as ProjectMembership[]).filter((m) => getProjectIdFromMembership(m) === projectId)
        : [];

    const memberships = fromGlobal.length > 0 ? fromGlobal : fromProject;

    const getUserFullName = (m: ProjectMembership): string | null => {
      const u: any = (m as any)?.user;
      if (!u) return null;
      if (typeof u === 'object') return (u.fullName as string) || null;
      if (typeof u === 'string') {
        const id = parseInt(u.split('/').pop() || '0', 10);
        if (id > 0) {
          return usersById.get(id) ?? `Utilisateur #${id}`;
        }
      }
      return null;
    };

    // Admin first (PROJECT_ADMIN > PROJECT_MANAGER)
    const adminMembership = memberships.find((m) => m.role === 'PROJECT_ADMIN') ?? memberships.find((m) => m.role === 'PROJECT_MANAGER');
    const adminName = adminMembership ? getUserFullName(adminMembership) : null;
    if (adminName) {
      return { name: adminName, kind: 'admin' };
    }

    // Otherwise: latest user associated to this project (by updatedAt/createdAt)
    if (memberships.length > 0) {
      const latest = [...memberships].sort((a, b) => getMembershipTimestamp(b) - getMembershipTimestamp(a))[0];
      const latestName = latest ? getUserFullName(latest) : null;
      if (latestName) {
        return { name: latestName, kind: 'member' };
      }
    }

    // No users
    return { name: 'Aucun utilisateur — ajoutez un membre', kind: 'none' };
  };

  // Enrich projects with stats
  const projectsWithStats: ProjectWithStats[] = useMemo(() => {
    if (!projects) return projects as ProjectWithStats[];

    return projects.map((project) => {
      // Get boxes for this project - handle both IRI string and object formats
      let projectBoxes: typeof allBoxes = [];
      if (project.boxes && Array.isArray(project.boxes) && project.boxes.length > 0) {
        projectBoxes = project.boxes;
      } else if (allBoxes) {
        projectBoxes = allBoxes.filter((box) => {
          const boxProjectId = typeof box.project === 'object' ? box.project?.id : 
            (typeof box.project === 'string' ? parseInt(box.project.split('/').pop() || '0') : null);
          return boxProjectId === project.id;
        });
      }
      const offlineBoxes = projectBoxes.filter((box) => !box.isOnline);

      const responsible = getResponsibleForProject(project.id, project);

      return {
        ...project,
        totalEquipments: projectBoxes.length,
        offlineEquipments: offlineBoxes.length,
        manager: responsible.name,
        managerKind: responsible.kind,
      };
    });
  }, [projects, allBoxes, membershipsData]);

  // Get latest project (API already returns projects ordered by createdAt DESC)
  const latestProject = useMemo(() => {
    if (!allProjectsResponse?.data || allProjectsResponse.data.length === 0) return null;
    
    // First project is already the latest (ordered by createdAt DESC from API)
    const latest = allProjectsResponse.data[0];
    if (!latest) return null;

    const responsible = getResponsibleForProject(latest.id, latest);

    return {
      ...latest,
      manager: responsible.name,
      managerKind: responsible.kind,
    };
  }, [allProjectsResponse?.data, membershipsData]);

  // Format alarm time
  const formatAlarmTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) {
      return `${diffMins}m`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else {
      return formatDate(dateString, 'dd MMM');
    }
  };

  // Get alarm icon and color based on type
  const getAlarmIcon = (alarm: Alarm) => {
    if (alarm.status === 'CLOSED') {
      return { icon: 'check_circle', bgClass: 'bg-emerald-500/10', textClass: 'text-emerald-500' };
    }
    // You can customize this based on alarm.type.code or other criteria
    if (alarm.title.toLowerCase().includes('offline') || alarm.title.toLowerCase().includes('hors ligne')) {
      return { icon: 'wifi_off', bgClass: 'bg-red-500/10', textClass: 'text-red-500' };
    }
    if (alarm.title.toLowerCase().includes('température') || alarm.title.toLowerCase().includes('temperature')) {
      return { icon: 'thermostat', bgClass: 'bg-orange-500/10', textClass: 'text-orange-500' };
    }
    if (alarm.title.toLowerCase().includes('utilisateur') || alarm.title.toLowerCase().includes('user')) {
      return { icon: 'person_add', bgClass: 'bg-blue-500/10', textClass: 'text-blue-500' };
    }
    return { icon: 'info', bgClass: 'bg-blue-500/10', textClass: 'text-blue-500' };
  };

  const getUserInitials = (name: string) => {
    if (!name) return 'AD';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Generate gradient colors for project icons
  const getProjectGradient = (index: number) => {
    const gradients = [
      'from-blue-500 to-cyan-400',
      'from-purple-500 to-pink-400',
      'from-emerald-500 to-teal-400',
      'from-indigo-500 to-blue-400',
      'from-slate-600 to-slate-400',
    ];
    return gradients[index % gradients.length];
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar 
        getUserInitials={getUserInitials} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white"
            >
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Tableau de Bord
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-7xl mx-auto space-y-8">
            {/* Page Heading */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 dark:text-white mb-2">
                  Tableau de Bord
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  Vue d'ensemble de la plateforme GTB et des indicateurs clés.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/admin/projects/new')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-slate-900 rounded-lg text-sm font-bold transition-colors shadow-lg"
                >
                  <span className="material-icons-outlined text-[20px]">add</span>
                  <span>Nouveau Projet</span>
                </button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* KPI Card 1: Total Projets */}
              <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl shadow-sm border border-slate-200 dark:border-[#1E293B] flex flex-col justify-between h-32 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-5 dark:opacity-10 transition-transform group-hover:scale-110">
                  <span className="material-icons-outlined text-8xl">folder_open</span>
                </div>
                <div className="flex justify-between items-start z-10">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium uppercase tracking-wider">
                      Total Projets
                    </p>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalProjects}</h3>
                  </div>
                  <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                    <span className="material-icons-outlined">folder</span>
                  </div>
                </div>
              </div>

              {/* KPI Card 2: Total Équipements */}
              <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl shadow-sm border border-slate-200 dark:border-[#1E293B] flex flex-col justify-between h-32 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-5 dark:opacity-10 transition-transform group-hover:scale-110">
                  <span className="material-icons-outlined text-8xl">dns</span>
                </div>
                <div className="flex justify-between items-start z-10">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium uppercase tracking-wider">
                      Total Équipements
                    </p>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                      {totalEquipments.toLocaleString()}
                    </h3>
                  </div>
                  <div className="p-2 bg-purple-500/10 rounded-lg text-purple-500">
                    <span className="material-icons-outlined">dns</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 font-medium mt-auto z-10">
                  <span>{onlinePercentage}% en ligne</span>
                </div>
              </div>

              {/* KPI Card 3: Total Utilisateurs */}
              <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl shadow-sm border border-slate-200 dark:border-[#1E293B] flex flex-col justify-between h-32 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-5 dark:opacity-10 transition-transform group-hover:scale-110">
                  <span className="material-icons-outlined text-8xl">group</span>
                </div>
                <div className="flex justify-between items-start z-10">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium uppercase tracking-wider">
                      Total Utilisateurs
                    </p>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalUsers}</h3>
                  </div>
                  <div className="p-2 bg-orange-500/10 rounded-lg text-orange-500">
                    <span className="material-icons-outlined">group</span>
                  </div>
                </div>
                {/* Note: We don't have createdAt for users, so we skip the trend for now */}
              </div>
            </div>

            {/* Split Content */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Column: Project Table (2/3) */}
              <div className="lg:w-2/3 flex flex-col gap-4">
                <div className="flex items-center justify-between px-1">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">État du Parc par Projet</h3>
                  <button
                    onClick={() => navigate('/admin/projects')}
                    className="text-sm text-primary hover:text-primary/80 font-medium"
                  >
                    Tout voir
                  </button>
                </div>
                <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                          <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Nom du Projet
                          </th>
                          <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Responsable
                          </th>
                          <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-center">
                            Total Éq.
                          </th>
                          <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Hors Ligne
                          </th>
                          <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {projectsLoading ? (
                          <tr>
                            <td colSpan={5} className="p-4 text-center text-slate-500 dark:text-slate-400">
                              Chargement...
                            </td>
                          </tr>
                        ) : projectsWithStats.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="p-4 text-center text-slate-500 dark:text-slate-400">
                              Aucun projet trouvé
                            </td>
                          </tr>
                        ) : (
                          projectsWithStats.map((project, index) => {
                            // Debug: log project data
                            if (process.env.NODE_ENV === 'development') {
                              console.log('Project:', project);
                            }
                            
                            return (
                              <tr
                                key={project.id}
                                className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                              >
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                  {project.logo ? (
                                    <img
                                      src={getProjectLogoUrl(project.logo) || ''}
                                      alt={project.title || 'Projet sans nom'}
                                      className="size-8 rounded object-cover"
                                      onError={(e) => {
                                        // Hide image if it fails to load
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                      <div
                                        className={`size-8 rounded bg-gradient-to-br ${getProjectGradient(index)}`}
                                      ></div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/admin/projects/${project.id}/areas`)}
                                      className="font-medium text-slate-900 dark:text-white hover:text-primary dark:hover:text-accent transition-colors text-left"
                                      title="Voir le projet"
                                    >
                                      {project.title || 'Projet sans nom'}
                                    </button>
                                  </div>
                                </td>
                                <td className="p-4 text-sm">
                                  {project.managerKind === 'none' ? (
                                    <span className="text-amber-700 dark:text-amber-300 font-medium">
                                      {project.manager}
                                    </span>
                                  ) : (
                                    <span className="text-slate-600 dark:text-slate-400">{project.manager || '-'}</span>
                                  )}
                                </td>
                                <td className="p-4 text-sm text-slate-600 dark:text-slate-400 text-center">
                                  {project.totalEquipments ?? 0}
                                </td>
                                <td className="p-4">
                                  {project.offlineEquipments && project.offlineEquipments > 0 ? (
                                    <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20 w-fit">
                                      <div className="size-2 rounded-full bg-red-500 animate-pulse"></div>
                                      <span className="text-xs font-bold text-red-500">
                                        {project.offlineEquipments} {project.offlineEquipments === 1 ? 'Critique' : 'Critiques'}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 w-fit">
                                      <span className="text-xs font-bold text-primary">0</span>
                                    </div>
                                  )}
                                </td>
                                <td className="p-4 text-right">
                                  <button
                                    onClick={() => navigate(`/admin/projects/${project.id}/areas`)}
                                    className="text-slate-400 hover:text-primary transition-colors"
                                    title="Voir le projet"
                                  >
                                    <span className="material-icons-outlined">visibility</span>
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right Column: Recent Activity (1/3) */}
              <div className="lg:w-1/3 flex flex-col gap-6">
                {/* Card: Dernier Projet */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white px-1">Dernier Projet</h3>
                  {latestProject ? (
                    <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 shadow-sm relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
                      <div className="flex items-start justify-between mb-4">
                        <ProjectLogoIcon project={latestProject} />
                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs font-medium text-slate-500">
                          Nouveau
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{latestProject.title}</h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        Créé le {formatDate(latestProject.createdAt, 'dd MMM, HH:mm')}
                      </p>
                      <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-700/50">
                        <div className="size-8 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {latestProject.managerKind === 'none'
                            ? '+'
                            : latestProject.manager
                              ? getUserInitials(latestProject.manager)
                              : 'AD'}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-400">Responsable</span>
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {latestProject.manager || '-'}
                          </span>
                          {latestProject.managerKind === 'none' && (
                            <button
                              onClick={() => navigate(`/admin/projects/${latestProject.id}/members`)}
                              className="mt-1 text-xs font-semibold text-primary hover:text-primary/80 text-left"
                            >
                              Ajouter un utilisateur
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 shadow-sm">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Aucun projet disponible</p>
                    </div>
                  )}
                </div>

                {/* Card: Journal des Alertes */}
                <div className="flex flex-col gap-4 flex-1">
                  <div className="flex items-center justify-between px-1">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Journal des Alertes</h3>
                    <button className="text-slate-400 hover:text-white">
                      <span className="material-icons-outlined">filter_list</span>
                    </button>
                  </div>
                  <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-0 shadow-sm overflow-hidden flex-1">
                    <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-700/50">
                      {alarmsResponse?.data && alarmsResponse.data.length > 0 ? (
                        alarmsResponse.data.map((alarm) => {
                          const { icon, bgClass, textClass } = getAlarmIcon(alarm);
                          return (
                            <div
                              key={alarm.id}
                              className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex gap-3"
                            >
                              <div className="flex-shrink-0 mt-1">
                                <div className={`size-8 rounded-full ${bgClass} flex items-center justify-center ${textClass}`}>
                                  <span className="material-icons-outlined text-sm">{icon}</span>
                                </div>
                              </div>
                              <div className="flex flex-col flex-1">
                                <div className="flex justify-between items-start">
                                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {alarm.title}
                                  </p>
                                  <span className="text-xs text-slate-500">{formatAlarmTime(alarm.createdAt)}</span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                  {alarm.box?.name || alarm.device?.name || 'Système'}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                          <p className="text-sm">Aucune alerte récente</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-700/50 text-center">
                      <button className="text-xs font-medium text-slate-500 hover:text-primary transition-colors">
                        Voir tout l'historique
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
